import React, { useEffect, useMemo, useState } from 'react';
import { resolveApiBase } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { PageRenderer } from '@/components/pagebuilder/PageRenderer';

export default function PreviewPage(){
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const id = window.location.pathname.split('/').pop();
  const API = resolveApiBase();

  useEffect(()=>{
    let mounted = true;
    fetch(`${API}/pages/${id}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r=>r.json())
      .then(j=>{ if(!mounted) return; if(j.error) setError(j.error); else setData(j); })
      .catch(e=> setError(e.message));
    return ()=>{ mounted=false; };
  }, [id, token]);

  const replacedTree = useMemo(()=>{
    if(!data?.page) return data?.page;
    const action = data.action || {};
    const company = data.company || {};
    const tokens = {
      '{{action.title}}': action.title || '',
      '{{action.companyName}}': action.companyName || action.company?.name || '',
      '{{action.campaignName}}': action.campaignName || action.campaign?.name || '',
      '{{action.discountType}}': action.discountType || '',
      '{{action.discountValue}}': action.discountValue != null ? String(action.discountValue) : '',
      '{{company.addressLine1}}': company.addressLine1 || '',
      '{{company.addressLine2}}': company.addressLine2 || '',
      '{{company.postalCode}}': company.postalCode || '',
      '{{company.city}}': company.city || '',
      '{{company.country}}': company.country || '',
    };
    const str = JSON.stringify(data.page);
    const replaced = Object.keys(tokens).reduce((acc,k)=> acc.split(k).join(tokens[k]), str);
    try { return JSON.parse(replaced); } catch { return data.page; }
  }, [data]);

  // Theme injection (same as PublicPage/RenderedPreview) + Google Fonts
  useEffect(()=>{
    if(!data?.theme) return;
    const colors = data.theme.colors || {};
    const styleElId = 'pb-theme';
    let el = document.getElementById(styleElId);
    if(!el){ el = document.createElement('style'); el.id = styleElId; document.head.appendChild(el); }
    const headingFont = data.theme.typography?.headingFont || '';
    const bodyFont = data.theme.typography?.bodyFont || '';
    const bodyStack = `var(--pb-font-body, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif)`;
    const headingStack = `var(--pb-font-heading, inherit)`;
    el.innerHTML = `:root{--pb-primary:${colors.primary || '#2563eb'};--pb-surface:${colors.surface || '#ffffff'};${bodyFont?`--pb-font-body:'${bodyFont}', system-ui;`:''}${headingFont?`--pb-font-heading:'${headingFont}', ${bodyFont || 'system-ui'};`:''}}
      .pb-root{ font-family: ${bodyStack}; }
      .pb-root h1,.pb-root h2,.pb-root h3,.pb-root h4,.pb-root h5,.pb-root h6{ font-family: ${headingStack}; }`;
    const addFontLink = (family, id)=>{
      if(!family) return;
      if(document.getElementById(id)) return;
      const f = family.replace(/ /g,'+');
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${f}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    };
    addFontLink(headingFont, 'pb-font-link-heading');
    addFontLink(bodyFont, 'pb-font-link-body');
  }, [data]);

  if(error) return <div className='p-6 text-red-600 text-sm'>{error}</div>;
  if(!data) return <div className='p-6 text-slate-500 text-sm'>Lade Vorschau...</div>;

  return (
    <div className='min-h-screen bg-background'>
      <div className='max-w-5xl mx-auto px-4 py-8'>
        <div className='text-[10px] text-slate-500 mb-2'>Vorschau (Server, ohne Persistenz)</div>
        <PageRenderer tree={replacedTree} meta={{ action: data.action || null, company: data.company || null }} />
      </div>
    </div>
  );
}
